import React, { FC, useState } from 'react';
import { Button, Icon, mergeTailwindClasses, Tooltip } from '@kontentino/ui';
import { faCopy } from '@fortawesome/pro-regular-svg-icons/faCopy';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons/faCalendar';
import clsx from 'clsx';
import useCopyToClipboard from 'app/hooks/useCopyToClipboard';

type ClassNames = {
  root?: string;
  loadingImg?: string;
  emptyImg?: string;
};

type Props = {
  suggestions: { text: string }[];
  isLoading: boolean;
  onBack: () => void;
  classNames?: ClassNames;
};

const CaptionGeneratedResults: FC<Props> = ({
  suggestions,
  classNames = {},
}) => {
  const isEmpty = suggestions.length === 0;
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const copyValueToClipboard = useCopyToClipboard();

  const copyToClipboard = (text: string, index: number) => {
    copyValueToClipboard(text);
    setCopiedIndex(index);

    setTimeout(() => {
      setCopiedIndex(null);
    }, 2000);
  };

  if (isEmpty) {
    return (
      <div
        className={mergeTailwindClasses(
          clsx(
            'tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-6',
            classNames.root,
          ),
        )}
      >
        <div
          className={mergeTailwindClasses(
            clsx(
              'tw-mb-4 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-grayscale-20',
              classNames.emptyImg || 'tw-h-20 tw-w-20',
            ),
          )}
        >
          <Icon icon={faCopy} className="tw-text-2xl tw-text-grayscale-100" />
        </div>
        <p className="tw-text-center tw-text-lg tw-text-grayscale-120">
          No captions generated
        </p>
      </div>
    );
  }

  return (
    <div
      className={mergeTailwindClasses(
        clsx('tw-flex tw-flex-col tw-gap-4', classNames.root),
      )}
    >
      <div className="tw-space-y-4">
        {suggestions.map((suggestion, index) => (
          <div
            key={`suggestion-${index}`}
            className="tw-rounded-lg tw-border tw-border-grayscale-20 tw-bg-white tw-p-4"
          >
            <div className="tw-grayscale-180 tw-mb-3 tw-flex tw-items-center tw-text-md tw-font-medium">
              <div className="tw-mr-auto tw-capitalize">
                Caption {suggestions.length > 1 ? index + 1 : ''}
              </div>
              <div className="tw-flex tw-items-center">
                <Tooltip content={'Schedule this post via Kontentino'}>
                  <a
                    href="https://www.kontentino.com/signup/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-name={`ai-caption-generator-schedule-${index}`}
                    className="tw-mr-3 tw-inline-flex tw-items-center tw-justify-center tw-rounded-md tw-bg-transparent tw-px-2 tw-py-1 tw-text-sm tw-font-medium tw-text-grayscale-120 hover:tw-bg-grayscale-10"
                  >
                    <Icon
                      icon={faCalendar}
                      className="tw-mr-1 tw-text-grayscale-100"
                    />
                    <span className="tw-text-sm tw-text-grayscale-120">
                      Schedule
                    </span>
                  </a>
                </Tooltip>
                <Tooltip
                  content={
                    copiedIndex === index ? 'Copied!' : 'Copy to clipboard'
                  }
                >
                  <Button
                    onClick={() => copyToClipboard(suggestion.text, index)}
                    data-name={`ai-caption-generator-copy-${index}`}
                    variant="plain"
                    size="small"
                    className="tw-mr-3"
                    iconBefore={
                      <Icon
                        icon={copiedIndex === index ? faCheck : faCopy}
                        className={
                          copiedIndex === index
                            ? 'tw-text-success-100'
                            : 'tw-text-grayscale-100'
                        }
                      />
                    }
                  >
                    <span className="tw-text-sm tw-text-grayscale-120">
                      {copiedIndex === index ? 'Copied!' : 'Copy'}
                    </span>
                  </Button>
                </Tooltip>
              </div>
            </div>
            <div
              className={mergeTailwindClasses(
                clsx(
                  'tw-whitespace-pre-wrap tw-break-words tw-rounded tw-bg-grayscale-5 tw-p-3 tw-text-md tw-text-grayscale-180',
                ),
              )}
            >
              {suggestion.text}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CaptionGeneratedResults;
